import { useEffect, useState } from "react";
import { useAdminTranslation, useCommonTranslation } from "@/i18n/hooks";
import { Button } from "@/components/tailwind/button";
import { Input } from "@/components/tailwind/input";
import { Text } from "@/components/tailwind/text";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, ExternalLink, Pencil, Plus, Trash, XCircle } from "lucide-react";
import { District } from "@/models/district";
import LoadingSpinner from "@/components/loader-spinner";
import {
    createDistrict,
    deleteDistrict,
    fetchAllDistricts,
    updateDistrict
} from "@/services/district-services";
import { useAuth } from "@/context/use-auth";
import ValidationAlert from "@/components/validation-alert";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import log from '@/lib/logger'

type DistrictItemProps = {
    district: District;
    editingId: string | null;
    handleStartEditing: (id: string) => void;
    handleSaveEditing: (id: string, name: string, abbreviation: string) => void;
    handleCancelEditing: () => void;
    handleDelete: (id: string) => void;
    t: (key: string, params?: Record<string, unknown>) => string;
    isUpdating: boolean;
};

function DistrictItem({
    district,
    editingId,
    handleStartEditing,
    handleSaveEditing,
    handleCancelEditing,
    handleDelete,
    t,
    isUpdating,
}: DistrictItemProps) {
    const [editName, setEditName] = useState(district.name);
    const [editAbbreviation, setEditAbbreviation] = useState(district.abbreviation || "");

    return (
        <div
            className="border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 bg-white dark:bg-zinc-900 shadow-sm"
            data-testid={`district-item-${district.id}`}
        >
            {editingId === district.id ? (
                <div className="space-y-3">
                    <div className="flex items-center gap-4">
                        <div className="grow">
                            <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                id={`edit-district-name-${district.id}`}
                                placeholder={t("adminSettings.districts.namePlaceholder")}
                                data-testid={`edit-district-name-${district.id}`}
                            />
                        </div>
                        <div className="w-32">
                            <Input
                                value={editAbbreviation}
                                onChange={(e) => setEditAbbreviation(e.target.value)}
                                id={`edit-district-abbreviation-${district.id}`}
                                placeholder={t("adminSettings.districts.abbreviationPlaceholder")}
                                data-testid={`edit-district-abbreviation-${district.id}`}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            plain
                            onClick={() => handleSaveEditing(district.id, editName, editAbbreviation)}
                            data-testid={`save-edit-district-${district.id}`}
                        >
                            <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                        </Button>
                        <Button
                            plain
                            onClick={handleCancelEditing}
                            data-testid={`cancel-edit-district-${district.id}`}
                        >
                            <XCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                        <Text className="text-zinc-900 dark:text-zinc-100 font-medium" data-testid={`district-name-${district.id}`}>
                            {district.name}
                        </Text>
                        {district.abbreviation && (
                            <span className="inline-flex items-center rounded-md bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-xs font-medium text-zinc-600 dark:text-zinc-400">
                                {district.abbreviation}
                            </span>
                        )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                        <Button
                            plain
                            onClick={() => handleStartEditing(district.id)}
                            title={t("adminSettings.districts.edit")}
                            disabled={isUpdating}
                            data-testid={`edit-district-${district.id}`}
                        >
                            <Pencil
                                className={`h-5 w-5 text-zinc-500 dark:text-zinc-400 ${!isUpdating ? "hover:text-blue-500 dark:hover:text-blue-400" : ""}`}
                            />
                        </Button>
                        <Button
                            plain
                            onClick={() => handleDelete(district.id)}
                            title={t("adminSettings.districts.delete")}
                            disabled={isUpdating}
                            data-testid={`delete-district-${district.id}`}
                        >
                            <Trash
                                className={`h-5 w-5 text-zinc-500 dark:text-zinc-400 ${!isUpdating ? "hover:text-red-500 dark:hover:text-red-400" : ""}`}
                            />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionDistricts() {
    const { t } = useAdminTranslation();
    const { t: tCommon } = useCommonTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();

    const [districts, setDistricts] = useState<District[]>([]);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [newName, setNewName] = useState<string>("");
    const [newAbbreviation, setNewAbbreviation] = useState<string>("");
    const [addingIssue, setAddingIssue] = useState<string | null>(null);
    const [editIssue, setEditIssue] = useState<string | null>(null);

    function showToast(title: string, description: string, variant: "default" | "destructive" = "default") {
        toast({
            title: title,
            description: description,
            variant: variant
        });
    }

    async function fetchDistricts() {
        setIsUpdating(true);
        try {
            const result = await fetchAllDistricts(token);
            if (result) {
                setDistricts(result);
            }
        } catch (error) {
            log.error("Error fetching districts:", error);
        }
        setIsUpdating(false);
    }

    useEffect(() => {
        fetchDistricts();
    }, [token]);

    function handleStartEditing(id: string) {
        setEditingId(id);
        setEditIssue(null);
    }

    function handleCancelEditing() {
        setEditingId(null);
        setEditIssue(null);
    }

    async function handleSaveEditing(id: string, name: string, abbreviation: string) {
        if (name.trim() === "") return;

        setIsUpdating(true);
        try {
            const result = await updateDistrict(token, id, {
                name: name.trim(),
                abbreviation: abbreviation.trim() || undefined
            });

            if (result) {
                showToast(
                    t("adminSettings.districts.toast.updated.title"),
                    t("adminSettings.districts.toast.updated.description", { name: name })
                );
                setEditingId(null);
                setEditIssue(null);
                await fetchDistricts();
            }
        } catch (error: unknown) {
            log.error(`Error updating district ${id}:`, error);
            const err = error as { validationIssues?: Array<{ message: string }>; status?: number; error?: string; message?: string };
            if (err.validationIssues) {
                setEditIssue(t(err.validationIssues[0].message));
            } else if (err.status) {
                setEditIssue(tCommon("serverError.responseCodeWithDetails", {
                    statusCode: err.status,
                    errorDetails: err.error
                }));
            } else {
                setEditIssue(tCommon("serverError.unknownWithDetails", { errorDetails: err.message }));
            }
        } finally {
            setIsUpdating(false);
        }
    }

    async function handleDelete(id: string) {
        setIsUpdating(true);
        try {
            await deleteDistrict(token, id);
            showToast(
                t("adminSettings.districts.toast.deleted.title"),
                t("adminSettings.districts.toast.deleted.description")
            );
            await fetchDistricts();
        } catch (error: unknown) {
            log.error("Error deleting district:", error);
            const err = error as { message?: string; status?: number };

            if (err.status === 409) {
                showToast(
                    t("adminSettings.districts.toast.deleteError.title"),
                    t("adminSettings.districts.cannotDelete"),
                    "destructive"
                );
            } else {
                showToast(
                    t("adminSettings.districts.toast.deleteError.title"),
                    t("adminSettings.districts.toast.deleteError.description"),
                    "destructive"
                );
            }
        } finally {
            setIsUpdating(false);
        }
    }

    async function handleAddNewDistrict() {
        if (newName.trim() === "") return;

        setIsUpdating(true);
        try {
            const result = await createDistrict(token, {
                name: newName.trim(),
                abbreviation: newAbbreviation.trim() || undefined
            });

            if (result) {
                showToast(
                    t("adminSettings.districts.toast.created.title"),
                    t("adminSettings.districts.toast.created.description", { name: result.name })
                );
                setNewName("");
                setNewAbbreviation("");
                setIsAdding(false);
                setAddingIssue(null);
                await fetchDistricts();
            }
        } catch (error: unknown) {
            log.error("Error creating district:", error);
            const err = error as { validationIssues?: Array<{ message: string }>; status?: number; error?: string; message?: string };
            if (err.validationIssues) {
                setAddingIssue(t(err.validationIssues[0].message));
            } else if (err.status) {
                setAddingIssue(tCommon("serverError.responseCodeWithDetails", {
                    statusCode: err.status,
                    errorDetails: err.error
                }));
            } else {
                setAddingIssue(tCommon("serverError.unknownWithDetails", { errorDetails: err.message }));
            }
        } finally {
            setIsUpdating(false);
        }
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            {isUpdating && (
                <div className="absolute inset-0 bg-white/60 dark:bg-zinc-950/60 flex items-center justify-center z-10 rounded-lg">
                    <div className="bg-white dark:bg-zinc-900 p-4 rounded-lg shadow-lg flex items-center gap-3">
                        <LoadingSpinner />
                        <Text>{tCommon("loading")}</Text>
                    </div>
                </div>
            )}

            <CardHeader>
                <div className="flex justify-between items-start gap-4">
                    <div className="max-w-2xl">
                        <CardTitle className="text-xl font-bold flex items-center gap-2">
                            {t("adminSettings.districts.title")}
                        </CardTitle>
                        <Text className="mt-1 text-sm text-muted-foreground">
                            {t("adminSettings.districts.description")}
                        </Text>
                    </div>
                    {!isAdding && (
                        <Button
                            outline
                            onClick={() => setIsAdding(true)}
                            disabled={isUpdating}
                            data-testid="add-district-button"
                            className="whitespace-nowrap"
                        >
                            <Plus className="h-5 w-5 mr-2" data-slot="icon" />
                            {t("adminSettings.districts.addNew")}
                        </Button>
                    )}
                </div>
            </CardHeader>

            {editIssue && (
                <ValidationAlert message={editIssue || tCommon("serverErrors.unknown")} />
            )}

            <CardContent>
                <div className="space-y-3 mb-6">
                    {districts.map((district) => (
                        <DistrictItem
                            key={district.id}
                            district={district}
                            editingId={editingId}
                            handleStartEditing={handleStartEditing}
                            handleSaveEditing={handleSaveEditing}
                            handleCancelEditing={handleCancelEditing}
                            handleDelete={handleDelete}
                            t={t}
                            isUpdating={isUpdating}
                        />
                    ))}
                    {districts.length === 0 && !isAdding && (
                        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                            <Text className="text-blue-700 dark:text-blue-300">
                                {t("adminSettings.districts.noItems")}
                            </Text>
                        </div>
                    )}
                </div>

                {isAdding && (
                    <>
                        <div className="space-y-3 mt-4 mb-2">
                            <div className="flex items-center gap-4">
                                <div className="grow">
                                    <Input
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        placeholder={t("adminSettings.districts.namePlaceholder")}
                                        data-testid="district-name"
                                    />
                                </div>
                                <div className="w-32">
                                    <Input
                                        value={newAbbreviation}
                                        onChange={(e) => setNewAbbreviation(e.target.value)}
                                        placeholder={t("adminSettings.districts.abbreviationPlaceholder")}
                                        data-testid="district-abbreviation"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    color="green"
                                    onClick={handleAddNewDistrict}
                                    data-testid="submit-district"
                                >
                                    {t("adminSettings.districts.saveNew")}
                                </Button>
                                <Button
                                    outline
                                    onClick={() => {
                                        setNewName("");
                                        setNewAbbreviation("");
                                        setIsAdding(false);
                                        setAddingIssue(null);
                                    }}
                                >
                                    {t("adminSettings.districts.cancel")}
                                </Button>
                            </div>
                        </div>
                        {addingIssue && (
                            <ValidationAlert message={addingIssue} />
                        )}
                    </>
                )}

                <div className="mt-6 pt-4 border-t border-zinc-200 dark:border-zinc-800">
                    <Button
                        outline
                        onClick={() => navigate("/admin/settings/district-streets")}
                        data-testid="manage-district-streets-link"
                    >
                        <ExternalLink className="h-4 w-4 mr-2" data-slot="icon" />
                        {t("adminSettings.districts.manageStreets")}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export default SectionDistricts;
